import { initializeApp } from "firebase/app";
import { getAnalytics } from "firebase/analytics";
import { getAuth, initializeAuth, indexedDBLocalPersistence, GoogleAuthProvider, setPersistence, browserLocalPersistence } from "firebase/auth";
import { Capacitor } from "@capacitor/core";
import { getFirestore, initializeFirestore, persistentLocalCache, persistentMultipleTabManager } from "firebase/firestore";
import { getStorage } from "firebase/storage";
import { getDatabase } from "firebase/database";
import { getMessaging, isSupported as isMessagingSupported } from "firebase/messaging";

// Chrome'un depolama bölümlemesi (storage partitioning) yüzünden signInWithRedirect, authDomain
// sitenin kendi alan adı değilse sonucu okuyamıyor: Google hesabı seçiliyor, sonra giriş ekranına
// geri dönülüyor (Android TWA + mobil Chrome). kapihoca.com Firebase Hosting'de olduğu için
// /__/auth/handler kendi alan adından sunulur. Diğer ortamlarda (localhost, native iOS) .env değeri kullanılır.
// Gereken: Google OAuth istemcisinde "https://kapihoca.com/__/auth/handler" yetkili yönlendirme URI'si.
const isProductionHost = typeof window !== 'undefined' && window.location.hostname === 'kapihoca.com';

// Firebase config .env dosyasından okunuyor (güvenlik için kaynak kodda tutulmaz)
const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: isProductionHost ? 'kapihoca.com' : import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
  measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID,
  databaseURL: import.meta.env.VITE_FIREBASE_DATABASE_URL || "https://tarih-atlas-kpss-default-rtdb.europe-west1.firebasedatabase.app"
};

let app;
let auth: any;
let db: any;
let storage: any;
let rtdb: any;
let messaging: any;
let googleProvider: any;
let isFirebaseActive = false;

try {
  app = initializeApp(firebaseConfig);
  
  // Analytics opsiyoneldir, hata verirse yoksay
  try { getAnalytics(app); } catch(e) { console.log("Analytics pasif."); }
  
  if (Capacitor.isNativePlatform()) {
    // Native (Capacitor iOS/Android): getAuth() popup/redirect iframe'i yüklemeye çalışır ve
    // capacitor:// WebView'de signInWithCredential sonsuza kadar takılı kalır (Apple giriş donması).
    // Bu yüzden resolver'sız, indexedDB kalıcılıklı initializeAuth kullanıyoruz.
    auth = initializeAuth(app, { persistence: indexedDBLocalPersistence });
  } else {
    auth = getAuth(app);
    // Oturumun tarayıcı kapatılsa bile kalıcı olması için persistence ayarını zorunlu yapıyoruz
    setPersistence(auth, browserLocalPersistence).catch((err) => {
      console.warn("⚠️ [Firebase] Oturum kalıcılığı ayarlanırken hata oluştu:", err);
    });
  }
  
  // Firestore'un kendi kalıcı önbelleği (IndexedDB): açılışta veri anında cihazdan gelir, dinleyiciler yeniden
  // bağlanınca yalnızca değişenler iner, çevrimdışı yazılar uygulama kapansa da kuyrukta kalır. Ana kaynak yine bulut:
  // userService dinleyicileri "buluttan yüklendi" bayrağını yalnızca metadata.fromCache === false iken kaldırır.
  // IndexedDB yoksa (gizli sekme vb.) SDK kendiliğinden bellek önbelleğine düşer.
  try {
    db = initializeFirestore(app, { localCache: persistentLocalCache({ tabManager: persistentMultipleTabManager() }) });
  } catch (e) {
    console.warn("[Firebase] Kalıcı Firestore önbelleği açılamadı, bellek önbelleği kullanılıyor:", e);
    db = getFirestore(app);
  }
  storage = getStorage(app);
  rtdb = getDatabase(app, firebaseConfig.databaseURL);
  // Web push (FCM JS) yalnızca destekleyen tarayıcılarda. Native uygulamada push native'dir (iOS: APNs, Android TWA:
  // FirebaseMessagingService); iOS WKWebView'de getMessaging() "messaging/unsupported-browser" ile yakalanmamış
  // promise hatası veriyordu (Sentry KAPIHOCA-A/C). `messaging` canlı bağlantıdır; kullanan kod null kontrolü yapar.
  if (!Capacitor.isNativePlatform()) {
    isMessagingSupported()
      .then((ok) => { if (ok) messaging = getMessaging(app); else console.warn("Messaging pasif: tarayıcı desteklemiyor."); })
      .catch((e) => console.warn("Messaging pasif:", e));
  }
  googleProvider = new GoogleAuthProvider();
  isFirebaseActive = true;
  console.log("🔥 Firebase Bağlantısı Başarılı (Bulut Modu)");
  
} catch (error) {
  console.warn("⚠️ Firebase bağlantısı kurulamadı. Yerel Veritabanı (Offline Mod) devreye giriyor.");
  console.error("Hata Detayı:", error);
  
  // --- MOCK SERVİSLER (YEREL MOD) ---
  auth = {
    currentUser: null,
    onAuthStateChanged: (callback: (user: any) => void) => {
       try {
           const localUser = localStorage.getItem('kpss_local_user');
           if (localUser) {
               const parsed = JSON.parse(localUser);
               auth.currentUser = parsed;
               callback(parsed);
           } else {
               auth.currentUser = null;
               callback(null);
           }
       } catch(e) { callback(null); }
       return () => {};
    },

    signOut: async () => {
        localStorage.removeItem('kpss_local_user');
        auth.currentUser = null;
        window.location.reload();
    }
  };
  
  db = { type: 'local_storage_db' };
  storage = null;
  rtdb = null;
  messaging = null;
  googleProvider = {};
  isFirebaseActive = false;
}

export { auth, db, storage, rtdb, messaging, googleProvider, isFirebaseActive };
export default app;
